import { Box, useTheme } from "@mui/material";
import { useMemo } from "react";

interface SchematicSvgProps {
  svg: string;
}

/**
 * Recolor a schemdraw-generated SVG for the dark theme.
 *
 * Schemdraw hard-codes black strokes / black fills on its symbols and
 * white "cutout" fills on enclosing shapes (so labels punch through
 * the wire bodies). On a navy chat surface a black-on-white card reads
 * as a bright island stapled to the page; recoloring lets the
 * schematic sit *inside* the surface instead of on top of it.
 *
 * Strategy: string-replace the four inline-style permutations
 * schemdraw emits, plus the `fill="black"` text attribute. No SVG
 * parsing — schemdraw's output is stable enough that a regex pass is
 * faster, simpler, and survives library updates.
 *
 * Color choices follow Circuit Forge's brand:
 *   • strokes:   `#9bb8d6` (desaturated cyan-blue) — readable on navy,
 *                still reads as "wire / pen" not "neon highlight."
 *   • text:      `#e6edf7` (theme `text.primary`).
 *   • cutouts:   the schematic's own background colour, so labels
 *                still punch a hole through wires correctly.
 *   • bg:        `#0d1424` (theme `surface.sunken`) — slightly darker
 *                than the chat surface so the schematic feels recessed.
 */
function recolorSchemdrawSvg(
  svg: string,
  opts: { bg: string; stroke: string; text: string },
): string {
  const { bg, stroke, text } = opts;
  return (
    svg
      // Filled symbols (diode triangles, ground bars, dots) — both
      // stroke and fill swap to the line colour.
      .replaceAll(
        "stroke:black;fill:black;",
        `stroke:${stroke};fill:${stroke};`,
      )
      // Cutout shapes — schemdraw fills the body of a wire-spanning
      // label rect with white so the wire visually breaks behind it.
      // Repaint that to the dark background so the cutout still works.
      .replaceAll("stroke:black;fill:white;", `stroke:${stroke};fill:${bg};`)
      // Plain stroked paths (wires, body outlines, transistor symbols).
      .replaceAll("stroke:black;fill:none;", `stroke:${stroke};fill:none;`)
      // Text labels — schemdraw uses the attribute form, not inline style.
      .replaceAll('fill="black"', `fill="${text}"`)
  );
}

/**
 * Inject a server-trusted schematic SVG (from `_render_subcircuit` /
 * `render_schematic`) as DOM, after recoloring schemdraw's hard-coded
 * black strokes for the dark theme. The SVG is sanitised upstream by
 * the renderer (server-trusted source, not user input), so
 * `dangerouslySetInnerHTML` is safe here.
 */
export function SchematicSvg({ svg }: SchematicSvgProps) {
  const theme = useTheme();
  const recolored = useMemo(() => {
    // Pull from theme rather than hardcode so a future scheme tweak
    // propagates to schematics without touching this file.
    const bg = theme.palette.surface.sunken;
    const stroke = "#9bb8d6";
    const text = theme.palette.text.primary;
    return recolorSchemdrawSvg(svg, { bg, stroke, text });
  }, [svg, theme.palette.surface.sunken, theme.palette.text.primary]);

  return (
    <Box
      sx={{
        bgcolor: "surface.sunken",
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "border.default",
        p: 1.5,
        my: 1.5,
        overflowX: "auto",
        "& svg": {
          maxWidth: "100%",
          height: "auto",
          display: "block",
        },
      }}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: schemdraw SVG is server-trusted, recolour pass only swaps colour literals
      dangerouslySetInnerHTML={{ __html: recolored }}
    />
  );
}
